import * as PIXI from 'pixi.js';

import { TypedSerializableMixin } from '../../serializableMixin';
import { TypedUpdateFlowMixin } from '../../updateFlowMixin';
import {
    AlphaAttribute,
    ColorAttribute,
    NumberAttribute,
    StringAttribute,
    getAttributesToSerialize,
} from '../../attributes';
import type { LoadSettings } from '../../serializableMixin';
import type { RawAssetData } from '../../../video-data/types';
import { Config } from '../../../video-data/config';
import { getCircleOffsetByRadiusAndStrokeWidth } from '../../CircleUtils';
import { int2rgba } from '../../../libs/videoData';
import { createLinearGradient } from '../../../libs/gradient';
import { GradientPoint } from '../../GradientPoint';
import type { DestroyOptions } from '../../../types';

/**
 * Draws a colored bounding box around the entire text(Metrics)
 *
 */
export class CircleGraphic extends TypedUpdateFlowMixin(TypedSerializableMixin(PIXI.Graphics)) {
    override _classType = 'CircleGraphic';
    override _name = Config.CIRCLE_GRAPHIC;

    @NumberAttribute({ min: 0 })
    accessor radius = 0;
    @NumberAttribute({ animatable: false })
    accessor positionX = 0;
    @NumberAttribute({ animatable: false })
    accessor positionY = 0;
    @ColorAttribute()
    accessor strokeColor = 16777215;
    @AlphaAttribute()
    accessor strokeAlpha = 1;
    @NumberAttribute({ min: 0, animatable: false })
    accessor strokeWidth = 0;
    @ColorAttribute()
    accessor fillColor = 16777215;
    @AlphaAttribute()
    accessor fillAlpha = 1;
    @StringAttribute({ choices: ['solid', 'linear'], animatable: false })
    accessor fillType: 'solid' | 'linear' = 'solid';

    gradientStartPoint = new GradientPoint({ parent: this, x: 0, y: 0.5, color: 0xff0000 });
    gradientEndPoint = new GradientPoint({ parent: this, x: 1, y: 0.5, color: 0x0000ff });

    #nonAnimatedRadius: number = 0;
    #canvas: HTMLCanvasElement | null = null;
    #gradientTexture: PIXI.Texture | null = null;
    #gradientTextureKey: string | null = null;

    #createGradientTexture(radius: number) {
        if (!this.#canvas) {
            this.#canvas = document.createElement('canvas');
        }
        const shapeDiameter = Math.max(1, Math.round(radius * 2));

        const start = this.gradientStartPoint;
        const end = this.gradientEndPoint;

        const key = [
            shapeDiameter,
            start.x,
            start.y,
            start.color,
            start.alpha,
            end.x,
            end.y,
            end.color,
            end.alpha,
        ].join(':');
        if (this.#gradientTexture && this.#gradientTextureKey === key) {
            return this.#gradientTexture;
        }

        // +1 px padding: UV at the bounding-box edge becomes diameter/(diameter+1) < 1.0,
        // preventing wrap-around artifacts regardless of the texture's wrapMode.
        const canvasSize = shapeDiameter + 1;
        this.#canvas.width = canvasSize;
        this.#canvas.height = canvasSize;

        const context = this.#canvas.getContext('2d');
        if (!context) return null;

        context.clearRect(0, 0, canvasSize, canvasSize);

        // The gradient points are normalized to the circle's bounding box (the diameter square).
        const gradient = createLinearGradient(context, {
            startX: start.x * shapeDiameter,
            startY: start.y * shapeDiameter,
            endX: end.x * shapeDiameter,
            endY: end.y * shapeDiameter,
            startStop: int2rgba(start.color, start.alpha),
            endStop: int2rgba(end.color, end.alpha),
        });

        context.fillStyle = gradient;
        context.fillRect(0, 0, canvasSize, canvasSize);

        if (this.#gradientTexture) {
            this.#gradientTexture.destroy(true);
        }
        this.#gradientTexture = PIXI.Texture.from(this.#canvas);
        this.#gradientTextureKey = key;
        return this.#gradientTexture;
    }

    override load(dataObj: RawAssetData, settings?: LoadSettings) {
        super.load(dataObj, settings);
        this.#nonAnimatedRadius = this.radius;
    }

    override _update() {
        this.clear();
        this.lineStyle(this.strokeWidth, this.strokeColor, this.strokeAlpha);

        const fillColor = this.fillType === 'solid' ? this.fillColor : 0xffffff;
        const fillAlpha = this.fillType === 'solid' ? this.fillAlpha : 1;

        const offset = getCircleOffsetByRadiusAndStrokeWidth(
            this.#nonAnimatedRadius,
            this.strokeWidth,
        );
        const circleCenterX = this.positionX + offset;
        const circleCenterY = this.positionY + offset;

        if (this.fillType === 'linear') {
            const texture = this.#createGradientTexture(this.radius);
            if (texture) {
                // beginTextureFill inverts the matrix before applying it, so pass
                // the positive offset (PIXI inverts to -(center-radius)) to align
                // the texture's top-left with the circle's bounding box.
                const matrix = new PIXI.Matrix();
                matrix.translate(circleCenterX - this.radius, circleCenterY - this.radius);
                this.beginTextureFill({ texture, matrix });
            } else {
                this.beginFill(fillColor, fillAlpha);
            }
        } else {
            this.beginFill(fillColor, fillAlpha);
        }

        this.drawCircle(circleCenterX, circleCenterY, this.radius);
        this.endFill();
    }

    override get propertiesToSerialize(): string[] {
        return getAttributesToSerialize(this).concat(['gradientStartPoint', 'gradientEndPoint']);
    }

    override destroy(options?: DestroyOptions) {
        if (this.#gradientTexture) {
            this.#gradientTexture.destroy(true);
            this.#gradientTexture = null;
            this.#gradientTextureKey = null;
        }
        super.destroy(options);
    }
}
